import { getLoadedEnv } from '@myre/common-conf';
import { LogType } from '@myre/common-logger';
import { DynamicEnvService } from '@myre/ui-domain-http';

import { HttpErrorResponse } from '@angular/common/http';
import {
  EnvironmentInjector,
  ErrorHandler,
  inject,
  Injectable,
} from '@angular/core';
import { Store, type ActionType } from '@ngrx/store';
import { addBreadcrumb, captureException } from '@sentry/angular';

import { getLatestValue } from '../../helpers/observable.helpers';

import { loggedUserSelector, type AuthState } from '../auth/auth.service';

export function isActionError(error: any): error is ActionType<{ error: any }> {
  return (
    error &&
    'type' in error &&
    typeof error.type === 'string' &&
    'error' in error
  );
}

// Matches cross-browser failed dynamic-import messages caused by a stale bundle after deploy.
const STALE_CHUNK_ERROR =
  /Failed to fetch dynamically imported module|error loading dynamically imported module|Importing a module script failed|ChunkLoadError/i;

export function isStaleChunkError(error: unknown): boolean {
  let message = '';

  if (error instanceof Error) {
    message = error.message;
  } else if (typeof error === 'string') {
    message = error;
  }

  return STALE_CHUNK_ERROR.test(message);
}

@Injectable({ providedIn: 'root' })
export class CustomErrorHandler extends ErrorHandler {
  private readonly _dynamicEnvService = inject(DynamicEnvService);
  private readonly _injector = inject(EnvironmentInjector);

  public override handleError(error: any): void {
    if (isStaleChunkError(error) && this._reloadOnceForStaleChunk()) {
      return;
    }

    const store = this._injector.get(Store<AuthState>);
    const loggedUser = getLatestValue(store.select(loggedUserSelector));

    if (this._dynamicEnvService.env.errors.sentry) {
      const actualError =
        isActionError(error) || error instanceof HttpErrorResponse
          ? error.error
          : error;

      const user = loggedUser && {
        ...loggedUser,
        id: loggedUser.id.toString(),
      };

      captureException(actualError, {
        user,
      });
    }

    if (this._dynamicEnvService.env.errors.console) {
      super.handleError(error);
    }
  }

  // Returns true and reloads once; debounce via sessionStorage prevents a reload loop.
  private _reloadOnceForStaleChunk(): boolean {
    if (typeof window === 'undefined') {
      return false;
    }

    const STORAGE_KEY = 'myre.staleChunkReloadAt';
    const RELOAD_DEBOUNCE_MS = 10_000;
    const now = Date.now();

    try {
      const lastReloadAt = Number(
        window.sessionStorage.getItem(STORAGE_KEY) ?? 0,
      );

      if (now - lastReloadAt < RELOAD_DEBOUNCE_MS) {
        return false;
      }

      window.sessionStorage.setItem(STORAGE_KEY, String(now));
    } catch {
      return false;
    }

    this._reload();

    return true;
  }

  // Isolated to allow stubbing in tests.
  protected _reload(): void {
    window.location.reload();
  }
}

export function logMessage(
  logType: LogType,
  message: string,
  details?: any,
): void {
  const env = getLoadedEnv();

  if (env.errors.sentry) {
    addBreadcrumb({
      message,
      category: logType,
      level: 'info',
      data: { details },
    });
  }

  if (env.errors.console) {
    // @ts-expect-error, eslint-disable-next-line no-console
    console.log(`[${LogType[logType]}]`, message, details);
  }
}
